import React, { useCallback } from 'react'
import { Circle, Group, Image as KonvaImage, Rect, Text } from 'react-konva'
import type { SideData } from '../types'
import type { MergedGroup } from '../hooks/useSideIndicators'
import { BOUNDARY_GAP, CELL_GAP, CELL_SIZE, COLORS, MAP_OFFSET_X, MAP_OFFSET_Y, SIDE_SIZE } from '../constants'
import type { ImageMap } from '../hooks/useImageLoader'

interface SideIndicatorsProps {
  xSides: SideData[]
  ySides: SideData[]
  mergedGroups: MergedGroup[]
  images?: ImageMap | null
  /** 텔레포트 5 기능 활성 */
  teleport5?: boolean
  /** 텔레포트 10 기능 활성 */
  teleport10?: boolean
  /** 텔레포트 비활성 (묶기가 시작되면 true) */
  teleportDisabled?: boolean
  /** 텔레포트 콜백 */
  onTeleport?: (targetLength: number) => void
  /** 현재 활성 텔레포트 (selecting/rotating/placing 중일 때) */
  activeTeleport?: { axis: 'row' | 'col'; targetLength: number } | null
  /** 텔레포트 취소 콜백 */
  onTeleportCancel?: () => void
  /** 뚱십 풀기 콜백 */
  onReleaseMergedGroup?: (groupId: string) => void
  /** 묶음 번호 맵 (key: "x-{index}", "y-{index}", "merged-{id}" → 번호) */
  bundleNumbers?: Map<string, number>
  /** 전체 비활성 (features.indicator === false) */
  disabled?: boolean
}

/** 표시등 번호: 1~9, 10번째는 "십" */
const SIDE_LABELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '십']

// 텔레포트 배경색
const TELEPORT_5_COLOR = '#66BB6A'
const TELEPORT_10_COLOR = '#2E7D32'
const TELEPORT_DISABLED_COLOR = '#CFD8DC'

/** x-side (top) 인덱스에 대한 x 좌표 */
function xSidePos(i: number) {
  const boundaryX = i >= 5 ? BOUNDARY_GAP : 0
  return MAP_OFFSET_X + i * (CELL_SIZE + CELL_GAP) + boundaryX + (CELL_SIZE - SIDE_SIZE) / 2
}

/** y-side (left) 인덱스에 대한 y 좌표 */
function ySidePos(i: number) {
  const boundaryY = i >= 5 ? BOUNDARY_GAP : 0
  return MAP_OFFSET_Y + i * (CELL_SIZE + CELL_GAP) + boundaryY + (CELL_SIZE - SIDE_SIZE) / 2
}

/**
 * 개별 표시등 렌더링
 * - off: 회색 배경 + 번호 표시
 * - full (not merged): 초록 배경 + "십" 텍스트
 * - 5/10번: 텔레포트 배경색 적용 (활성 시)
 */
function SideIndicator({
  x,
  y,
  state,
  index,
  images,
  isTeleport,
  teleportColor,
  teleportDisabled,
  bundleNumber,
  onClick,
}: {
  x: number
  y: number
  state: string
  index: number
  images?: ImageMap | null
  isTeleport?: boolean
  teleportColor?: string
  teleportDisabled?: boolean
  bundleNumber?: number
  onClick?: () => void
}) {
  const isFull = state === 'full'
  const label = isFull ? (bundleNumber != null ? String(bundleNumber) : '십') : SIDE_LABELS[index]

  // 텔레포트 표시등: 배경색 결정
  const hasTeleportBg = isTeleport && !isFull
  const bgColor = hasTeleportBg
    ? (teleportDisabled ? TELEPORT_DISABLED_COLOR : teleportColor)
    : undefined

  const sideImg = isFull ? (images?.sideFull ?? null) : (images?.sideOff ?? null)
  const canClick = isTeleport && !isFull && !teleportDisabled

  const handleClick = useCallback(() => {
    if (canClick && onClick) onClick()
  }, [canClick, onClick])

  return (
    <Group onClick={canClick ? handleClick : undefined} onTap={canClick ? handleClick : undefined}>
      {hasTeleportBg ? (
        // 텔레포트 표시등: 커스텀 배경색
        <Rect
          x={x} y={y}
          width={SIDE_SIZE} height={SIDE_SIZE}
          fill={bgColor}
          cornerRadius={3}
          stroke={COLORS.gridBorder}
          strokeWidth={1}
        />
      ) : sideImg ? (
        <KonvaImage x={x} y={y} width={SIDE_SIZE} height={SIDE_SIZE} image={sideImg} listening={false} />
      ) : (
        <Rect
          x={x} y={y}
          width={SIDE_SIZE} height={SIDE_SIZE}
          fill={isFull ? COLORS.sideFull : COLORS.sideOff}
          cornerRadius={3}
          stroke={COLORS.gridBorder}
          strokeWidth={1}
        />
      )}
      <Text
        x={x} y={y}
        width={SIDE_SIZE} height={SIDE_SIZE}
        text={label}
        fontSize={isFull ? 18 : 16}
        fontStyle="bold"
        fontFamily="CookeiRunBold"
        fill={hasTeleportBg && !teleportDisabled ? '#FFFFFF' : isFull ? '#FFFFFF' : teleportDisabled && isTeleport ? '#90A4AE' : '#FFFFFF'}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
    </Group>
  )
}

/**
 * 합쳐진 표시등 렌더링 (뚱십)
 * - 1칸 크기, 합쳐진 범위의 중앙에 배치 + "십" 텍스트
 * - 우측 상단에 작은 풀기 버튼
 */
function MergedIndicator({
  group,
  images,
  onRelease,
  bundleNumber,
}: {
  group: MergedGroup
  images?: ImageMap | null
  onRelease?: (groupId: string) => void
  bundleNumber?: number
}) {
  const { id, dir, indices } = group
  const sorted = [...indices].sort((a, b) => a - b)
  const first = sorted[0]
  const last = sorted[sorted.length - 1]

  let x: number, y: number

  if (dir === 'x') {
    const firstX = xSidePos(first)
    const lastX = xSidePos(last) + SIDE_SIZE
    x = (firstX + lastX) / 2 - SIDE_SIZE / 2
    y = MAP_OFFSET_Y - SIDE_SIZE - 6
  } else {
    x = MAP_OFFSET_X - SIDE_SIZE - 6
    const firstY = ySidePos(first)
    const lastY = ySidePos(last) + SIDE_SIZE
    y = (firstY + lastY) / 2 - SIDE_SIZE / 2
  }

  const fullImg = images?.sideFull ?? null

  const handleRelease = useCallback(() => {
    onRelease?.(id)
  }, [onRelease, id])

  // 풀기 버튼 위치: 우측 상단
  const releaseBtnSize = 14
  const releaseBtnX = x + SIDE_SIZE - releaseBtnSize + 2
  const releaseBtnY = y - 2

  return (
    <Group>
      {fullImg ? (
        <KonvaImage x={x} y={y} width={SIDE_SIZE} height={SIDE_SIZE} image={fullImg} listening={false} />
      ) : (
        <Rect x={x} y={y} width={SIDE_SIZE} height={SIDE_SIZE} fill={COLORS.sideFull} cornerRadius={3} stroke={COLORS.gridBorder} strokeWidth={1} />
      )}
      <Text
        x={x} y={y}
        width={SIDE_SIZE} height={SIDE_SIZE}
        text={bundleNumber != null ? String(bundleNumber) : '십'}
        fontSize={18}
        fontStyle="bold"
        fontFamily="CookeiRunBold"
        fill="#FFFFFF"
        align="center"
        verticalAlign="middle"
        listening={false}
      />
      {/* 풀기 버튼 (우측 상단) */}
      {onRelease && (
        <Group onClick={handleRelease} onTap={handleRelease}>
          <Circle
            x={releaseBtnX + releaseBtnSize / 2}
            y={releaseBtnY + releaseBtnSize / 2}
            radius={releaseBtnSize / 2}
            fill="#FF5252"
            stroke="#FFFFFF"
            strokeWidth={1.5}
          />
          <Text
            x={releaseBtnX}
            y={releaseBtnY}
            width={releaseBtnSize}
            height={releaseBtnSize}
            text="✕"
            fontSize={9}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            listening={false}
          />
        </Group>
      )}
    </Group>
  )
}

export const SideIndicators: React.FC<SideIndicatorsProps> = ({
  xSides,
  ySides,
  mergedGroups,
  images,
  teleport5 = false,
  teleport10 = false,
  teleportDisabled = false,
  onTeleport,
  activeTeleport,
  onTeleportCancel,
  onReleaseMergedGroup,
  bundleNumbers,
  disabled = false,
}) => {
  // merged group에 포함된 인덱스 Set
  const xMergedIndices = new Set<number>()
  const yMergedIndices = new Set<number>()
  for (const g of mergedGroups) {
    const set = g.dir === 'x' ? xMergedIndices : yMergedIndices
    for (const idx of g.indices) set.add(idx)
  }

  // 텔레포트 인덱스 판별 (0-based: index 4 = 5번째, index 9 = 10번째)
  const isTeleportIndex = (index: number): { is: boolean; color?: string; length?: number } => {
    if (index === 4 && teleport5) return { is: true, color: TELEPORT_5_COLOR, length: 5 }
    if (index === 9 && teleport10) return { is: true, color: TELEPORT_10_COLOR, length: 10 }
    return { is: false }
  }

  return (
    <Group opacity={disabled ? 0.4 : 1} listening={!disabled}>
      {/* X sides (top) */}
      {xSides.map((side, i) => {
        if (xMergedIndices.has(i)) return null
        const tp = isTeleportIndex(i)
        return (
          <SideIndicator
            key={`side-x-${i}`}
            x={xSidePos(i)}
            y={MAP_OFFSET_Y - SIDE_SIZE - 6}
            state={side.state}
            index={i}
            images={images}
            isTeleport={tp.is}
            teleportColor={tp.color}
            teleportDisabled={teleportDisabled}
            bundleNumber={bundleNumbers?.get(`x-${i}`)}
            onClick={tp.is ? () => onTeleport?.(tp.length!) : undefined}
          />
        )
      })}
      {/* Y sides (left) */}
      {ySides.map((side, i) => {
        if (yMergedIndices.has(i)) return null
        const tp = isTeleportIndex(i)
        return (
          <SideIndicator
            key={`side-y-${i}`}
            x={MAP_OFFSET_X - SIDE_SIZE - 6}
            y={ySidePos(i)}
            state={side.state}
            index={i}
            images={images}
            isTeleport={tp.is}
            teleportColor={tp.color}
            teleportDisabled={teleportDisabled}
            bundleNumber={bundleNumbers?.get(`y-${i}`)}
            onClick={tp.is ? () => onTeleport?.(tp.length!) : undefined}
          />
        )
      })}
      {/* Merged groups (뚱십) */}
      {mergedGroups.map((g) => (
        <MergedIndicator
          key={g.id}
          group={g}
          images={images}
          onRelease={onReleaseMergedGroup}
          bundleNumber={bundleNumbers?.get(`merged-${g.id}`)}
        />
      ))}
      {/* 텔레포트 취소 버튼 (활성 텔레포트 버튼 우상단) */}
      {activeTeleport && onTeleportCancel && (() => {
        const tpIndex = activeTeleport.targetLength === 5 ? 4 : 9
        const btnSize = 14
        let btnX: number, btnY: number
        if (activeTeleport.axis === 'col') {
          // col축: 세로줄 선택 → 상단(x-side) 표시등에 취소 버튼
          btnX = xSidePos(tpIndex) + SIDE_SIZE - btnSize + 2
          btnY = (MAP_OFFSET_Y - SIDE_SIZE - 6) - 2
        } else {
          // row축: 가로줄 선택 → 좌측(y-side) 표시등에 취소 버튼
          btnX = (MAP_OFFSET_X - SIDE_SIZE - 6) + SIDE_SIZE - btnSize + 2
          btnY = ySidePos(tpIndex) - 2
        }
        return (
          <Group onClick={onTeleportCancel} onTap={onTeleportCancel}>
            <Circle
              x={btnX + btnSize / 2}
              y={btnY + btnSize / 2}
              radius={btnSize / 2}
              fill="#FF5252"
              stroke="#FFFFFF"
              strokeWidth={1.5}
            />
            <Text
              x={btnX}
              y={btnY}
              width={btnSize}
              height={btnSize}
              text="✕"
              fontSize={9}
              fill="#FFFFFF"
              align="center"
              verticalAlign="middle"
            />
          </Group>
        )
      })()}
    </Group>
  )
}
